import { useCallback, useEffect, useRef, useState } from 'react';
import { combineCuts, zipCuts } from '../../helpers';
import {
  PART_REQUEST_TIMEOUT_MS,
  PART_RETRY_DELAYS_MS,
  describeDownloadError,
  openDownloadOrOffer,
  prepareDownloadCuts,
  preparePart,
  splitIntoParts,
  toCutRequest,
} from '../../helpers/playlistDownloadParts';
import { trackVideoAction } from '../../tracking';

const withDownloadParams = (path, extraParams = {}) => {
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${new URLSearchParams({ download: 'true', ...extraParams })}`;
};

const requestZipPart = async (part, signal) => {
  const result = await zipCuts(part.cuts.map(toCutRequest), {
    signal,
    timeout: PART_REQUEST_TIMEOUT_MS,
  });
  const path = result?.data?.path;

  return path ? withDownloadParams(path) : null;
};

// Downloads playlist clips through the video service. A download that fits in one
// part is prepared as a single file (one video or one ZIP, as before); a longer one
// is prepared as ZIP parts, one at a time, each with its own Download link.
export const usePlaylistDownload = ({
  addNotification,
  removeNotification,
  retryDelaysMs = PART_RETRY_DELAYS_MS,
}) => {
  const [preparingFile, setPreparingFile] = useState(false);
  const [partsDownload, setPartsDownload] = useState(null);
  const partsDownloadRef = useRef(null);
  const controllerRef = useRef(null);
  const busyRef = useRef(false);

  useEffect(() => {
    partsDownloadRef.current = partsDownload;
  }, [partsDownload]);

  useEffect(() => () => controllerRef.current?.abort(), []);

  const running = Boolean(partsDownload?.running);

  // Parts are prepared by this page, so leaving it stops them.
  useEffect(() => {
    if (!running) return undefined;

    const warnBeforeLeaving = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeLeaving);
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
  }, [running]);

  const updatePart = useCallback((downloadId, number, update) => {
    setPartsDownload((current) => (current?.id === downloadId
      ? {
        ...current,
        parts: current.parts.map((part) => (part.number === number ? { ...part, ...update } : part)),
      }
      : current));
  }, []);

  const runParts = useCallback(async (downloadId, parts) => {
    const controller = new AbortController();
    const queued = new Set(parts.map((part) => part.number));
    controllerRef.current = controller;
    busyRef.current = true;

    setPartsDownload((current) => (current?.id === downloadId
      ? {
        ...current,
        running: true,
        parts: current.parts.map((part) => (queued.has(part.number)
          ? { ...part, status: 'waiting', error: null }
          : part)),
      }
      : current));

    // One part at a time: parallel parts would compete for the same video workers
    // and push every part past the router's time limit.
    for (const part of parts) {
      if (controller.signal.aborted) break;

      await preparePart({
        part,
        requestPart: requestZipPart,
        onChange: (update) => updatePart(downloadId, part.number, update),
        signal: controller.signal,
        retryDelaysMs,
      });
    }

    if (controllerRef.current === controller) {
      controllerRef.current = null;
      busyRef.current = false;
    }

    setPartsDownload((current) => (current?.id === downloadId
      ? {
        ...current,
        running: false,
        parts: current.parts.map((part) => (part.status === 'waiting' ? { ...part, status: 'stopped' } : part)),
      }
      : current));
  }, [retryDelaysMs, updatePart]);

  const downloadSingleFile = useCallback(async ({ cuts, mode }) => {
    const controller = new AbortController();
    const notificationId = `download-${Date.now()}`;
    controllerRef.current = controller;
    setPreparingFile(true);
    document.body.classList.add('loading-cursor');
    addNotification('Preparing your download…', 'info', { controller, id: notificationId, duration: 0 });

    try {
      const body = cuts.map(toCutRequest);
      const config = { signal: controller.signal, timeout: PART_REQUEST_TIMEOUT_MS };
      let url = null;

      if (mode === 'video') {
        // Keep this batch distinct through proxies and the video service. This
        // prevents a download from resolving to a previous batch's output.
        const requestId = window.crypto?.randomUUID?.()
          || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const result = await combineCuts(body, { ...config, params: { request_id: requestId } });
        url = result?.data?.path ? withDownloadParams(result.data.path, { request_id: requestId }) : null;
      } else {
        const result = await zipCuts(body, config);
        url = result?.data?.path ? withDownloadParams(result.data.path) : null;
      }

      if (!url) {
        addNotification("The video service didn't return a file. Please try again.", 'error');
        return;
      }

      openDownloadOrOffer(url, (readyUrl) => addNotification('Your download is ready.', 'success', {
        actionLabel: 'Download',
        actionUrl: readyUrl,
        duration: 0,
      }));
    } catch (error) {
      if (!controller.signal.aborted) {
        addNotification(describeDownloadError(error, 'this download'), 'error');
      }
    } finally {
      document.body.classList.remove('loading-cursor');
      removeNotification(notificationId);
      if (controllerRef.current === controller) {
        controllerRef.current = null;
      }
      busyRef.current = false;
      setPreparingFile(false);
    }
  }, [addNotification, removeNotification]);

  // mode: 'video' for one combined video, 'zip' for a ZIP of separate clips.
  // tracking: trackVideoAction fields plus `downloadMode` for a single-file download.
  const startDownload = useCallback(async ({ videos, mode = 'zip', title = '', tracking = {} }) => {
    if (busyRef.current) return;

    const { cuts, skipped } = prepareDownloadCuts(videos);

    if (!cuts.length) {
      addNotification('There are no videos available for download', 'info');
      return;
    }

    if (skipped.length) {
      addNotification(`${skipped.length} clip(s) without a valid video source or time range were skipped.`, 'info');
    }

    const parts = splitIntoParts(cuts);
    const footageSeconds = parts.reduce((total, part) => total + part.seconds, 0);
    const { downloadMode, ...trackingFields } = tracking;

    trackVideoAction({
      ...trackingFields,
      action: 'download',
      metadata: {
        download_mode: parts.length > 1 ? 'zip_parts' : downloadMode,
        footage_seconds: footageSeconds,
        part_count: parts.length,
        selected_count: cuts.length,
        skipped_count: skipped.length,
        video_ids: videos.map((item) => item?.id).filter(Boolean),
      },
    });

    busyRef.current = true;

    if (parts.length === 1) {
      await downloadSingleFile({ cuts, mode });
      return;
    }

    const downloadId = Date.now();
    setPartsDownload({
      id: downloadId,
      title,
      clipCount: cuts.length,
      skippedCount: skipped.length,
      footageSeconds,
      running: true,
      parts: parts.map((part) => ({
        number: part.number,
        cuts: part.cuts,
        seconds: part.seconds,
        firstClip: part.cuts[0].index + 1,
        lastClip: part.cuts[part.cuts.length - 1].index + 1,
        status: 'waiting',
        url: null,
        error: null,
        downloaded: false,
      })),
    });
    runParts(downloadId, parts);
  }, [addNotification, downloadSingleFile, runParts]);

  const stopDownload = useCallback(() => {
    controllerRef.current?.abort();
  }, []);

  const resumeDownload = useCallback(() => {
    const current = partsDownloadRef.current;
    if (!current || busyRef.current) return;

    const remaining = current.parts.filter((part) => part.status !== 'ready');
    if (remaining.length) {
      runParts(current.id, remaining);
    }
  }, [runParts]);

  const closeDownload = useCallback(() => {
    controllerRef.current?.abort();
    setPartsDownload(null);
  }, []);

  const markPartDownloaded = useCallback((number) => {
    const current = partsDownloadRef.current;
    if (current) {
      updatePart(current.id, number, { downloaded: true });
    }
  }, [updatePart]);

  return {
    isBusy: preparingFile || running,
    partsDownload,
    startDownload,
    stopDownload,
    resumeDownload,
    closeDownload,
    markPartDownloaded,
  };
};
